import React, { useState, useRef } from 'react';
import * as Yup from 'yup';
import { Formik, Form, Field, useField } from 'formik';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormHelperText,
  Grid,
  Typography,
  IconButton,
  Checkbox,
  FormControlLabel,
  Tooltip,
} from '@mui/material';
import { useSelector } from 'react-redux';
import InventoryListAPIs from '../../../utilities/api/inventory/AdminInventoryList';
import CustomLocationDialog from './CustomLocation';
import CustomCategoryDialog from './CustomCategory';
import CustomGroupDialog from './CustomGroup';
import InventoryLocationAPIs from '../../../utilities/api/location/AdminLocationList';
import InventoryCategoryAPIs from '../../../utilities/api/category/AdminInventoryCategoryList';
import InventoryGroupAPIs from '../../../utilities/api/group/AdminInventoryGroupList';
import { toast } from 'react-toastify';
import FormField from '../../shared/FormField';
import { KeyboardArrowDown } from '@mui/icons-material';
import DocumentScannerOutlinedIcon from '@mui/icons-material/CropFree';

const taglist = [
  'Free',
  'Issue',
  'Purchase',
  'Exhaustible',
  'NonExhaustible',
  'InLabOnly',
  'HighValue',
];
const availabilityList = ['Moderate', 'Very Low', 'Low', 'High', 'Always'];
const statusList = ['Operational', 'Under Maintenance', 'Out of Service'];

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Product type is required'),
  // upc: Yup.number().required('UPC is required'),
  upc: Yup.string().matches(/^\d*$/, 'UPC must be a pure number.'),
  stock_available: Yup.number()
    .required('Stock available is required')
    .test(
      'is-lte',
      'Stock available cannot be more than stock total',
      function (value) {
        return value <= this.parent.stock_total;
      }
    ),
  stock_total: Yup.number().required('Stock total is required'),
  instances: Yup.number().required('Instances is required'),
  category: Yup.string(),
  location: Yup.string(),
  availability: Yup.string(),
  status: Yup.string(),
  supervised: Yup.boolean(),
  tags: Yup.array()
    .required('This field is required')
    .test(
      'one-of-three',
      'Only one of Free, Issue, Purchase can be selected',
      value => {
        const count = ['Free', 'Issue', 'Purchase'].filter(tag =>
          value.includes(tag)
        ).length;
        return count <= 1;
      }
    )
    .test(
      'one-of-required-three',
      'At least one of Free, Issue, Purchase must be selected',
      value => {
        return ['Free', 'Issue', 'Purchase'].some(tag => value.includes(tag));
      }
    )
    .test(
      'exclusivity',
      'Exhaustible and NonExhaustible cannot be selected together',
      value => {
        return !(
          value.includes('Exhaustible') && value.includes('NonExhaustible')
        );
      }
    ),
  manufacturer: Yup.string(),
  purchase_cost: Yup.string(),
  image: Yup.mixed(),
  // description: Yup.string().required('This field is required'),
  description: Yup.string(),
  stock_unit: Yup.string(),
});

const styledField = {
  maxWidth: '100%',
  width: '100%',
  padding: '0px 4px 8px 6px',
  '& .MuiFormLabel-root': {
    marginLeft: '4px',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderRadius: '12px',
    border: '1px solid #8C8C8C',
  },
};
const headerProps = { sx: { fontWeight: '400', color: '#666666' } };

const RenderFileUpload = ({ name, sx }) => {
  const [meta, helpers] = useField(name);
  const [preview, setPreview] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileChange = event => {
    const file = event.currentTarget.files[0];

    if (file) {
      // Check if file size exceeds 1MB (1MB = 1,048,576 bytes)
      const fileSizeLimit = 1048576; // 1MB in bytes
      if (file.size > fileSizeLimit) {
        toast.error('File size should not exceed 1MB');
        return; // Stop the upload process if the file is too large
      }

      // Check if the file is an image
      if (!file.type.startsWith('image/')) {
        toast.error('Only image files are allowed!');
        return;
      }

      // If both validations pass, set the image and display a preview
      helpers.setValue(file);
      setPreview(URL.createObjectURL(file));
    }
  };

  return (
    <Box>
      <input
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        ref={fileInputRef}
        onChange={handleFileChange}
      />
      <Button
        variant="contained"
        onClick={() => fileInputRef.current.click()}
        sx={{
          height: preview ? '40px' : '100px',
          width: '150px',
          padding: preview ? '8px 12px' : '12px 16px',
          margin: 1,
          background: preview ? '#FFE6E6' : '#E6F2FF',
          color: '#3F3F3F',
          border: preview ? '1px solid #FF6666' : '1px solid #66B0FF',
          borderRadius: '8px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
          '&:hover': {
            background: preview ? '#FFE6E6' : '#E6F2FF',
            color: '#3F3F3F',
            border: preview ? '1px solid #FF6666' : '1px solid #66B0FF',
          },
        }}
      >
        <Typography
          sx={{ fontSize: preview ? 14 : 24, p: 0, m: 0, mt: preview ? 0 : -1 }}
        >
          {preview ? 'Change Image' : '+'}
        </Typography>
        {!preview && (
          <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
            Upload File
          </Typography>
        )}
      </Button>
      {meta.touched && meta.error && (
        <FormHelperText error>{meta.error}</FormHelperText>
      )}
      {preview && (
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <img
            src={preview}
            alt="Preview"
            style={{ maxWidth: '100%', height: 'auto' }}
          />
        </Box>
      )}
    </Box>
  );
};

const AddInventoryDialog = ({
  open,
  onClose,
  update,
  setUpdate,
  flag,
  setFlag,
  setScannerOpen,
}) => {
  const user = useSelector(state => state.user.user);
  const token = user.access;

  const [openCustomLocation, setOpenCustomLocation] = useState(false);
  const [customLocation, setCustomLocation] = useState('');

  const [openCustomCategory, setOpenCustomCategory] = useState(false);
  const [customCategory, setCustomCategory] = useState('');

  const [openCustomGroup, setOpenCustomGroup] = useState(false);
  const [customGroup, setCustomGroup] = useState('');

  const category = useSelector(state => state.category.category_list);
  const location = useSelector(state => state.location.location_list);
  const group = useSelector(state => state.group.group_list);

  const categories = category.map(item => item.name);
  const locations = location.map(item => item.name);
  const groups = group.map(item => item.name);

  const handleConfirmLocation = () => {
    setOpenCustomLocation(false);
    const response = InventoryLocationAPIs.LocationListPost(customLocation);
    response
      .then(result => {
        console.log(result);
        toast.success('Location added successfully');
        setFlag(prev => !prev);
      })
      .catch(err => {
        console.log('error', err);
        toast.error('Failed to add location');
      });
  };

  const handleConfirmCategory = () => {
    setOpenCustomCategory(false);
    const response = InventoryCategoryAPIs.CategoryListPost(customCategory);
    response
      .then(result => {
        console.log(result);
        toast.success('Category added successfully');
        setFlag(prev => !prev);
      })
      .catch(err => {
        console.log('error', err);
        toast.error('Failed to add category');
      });
  };

  const handleConfirmGroup = () => {
    setOpenCustomGroup(false);
    const response = InventoryGroupAPIs.GroupListPost(customGroup);
    response
      .then(result => {
        console.log(result);
        toast.success('Group added successfully');
        setFlag(prev => !prev);
      })
      .catch(err => {
        console.log('error', err);
        toast.error('Failed to add group');
      });
  };

  const handleFormSubmit = async (values, actions) => {
    console.log('handleFormSubmit', values);
    const formData = new FormData();

    // Append all form values to formData except tags
    for (const key in values) {
      if (values.hasOwnProperty(key) && key !== 'tags') {
        formData.append(key, values[key]);
      }
    }

    // Define a mapping for the tag values
    const tagMapping = {
      Free: '0',
      Issue: '1',
      Purchase: '2',
      Exhaustible: '3',
      NonExhaustible: '4',
      InLabOnly: '5',
      HighValue: '6',
    };

    // Map the selected tags to `tag1`, `tag2`, etc.
    values.tags.forEach((tag, index) => {
      const tagKey = `tag${index + 1}`;
      const tagValue = tagMapping[tag] || ''; // Default to empty if not found in mapping
      formData.append(tagKey, tagValue);
    });

    // Append mapped availability and status values
    if (values.availability) {
      formData.append(
        'availability1',
        availabilityList.indexOf(values.availability).toString()
      );
    }
    if (values.status) {
      formData.append('status1', statusList.indexOf(values.status).toString());
    }

    try {
      const response = await InventoryListAPIs.AddInventory(formData, token);
      console.log(response);
      toast.success('Product added successfully');
      onClose();
      setUpdate(prev => !prev);
      actions.resetForm();
    } catch (error) {
      console.log(error);
      if (error.response && error.response.data) {
        const errorMessages = Object.values(error.response.data)
          .flat()
          .join(' ');
        toast.error(`Failed to add product: ${errorMessages}`);
        actions.setErrors({ submit: errorMessages });
      } else {
        toast.error('Failed to add product: An unexpected error occurred.');
      }
      actions.setSubmitting(false);
    }
  };

  const handleFormClose = resetForm => {
    onClose();
    // resetForm();
    toast.info('Form discarded');
  };

  return (
    <Dialog
      open={open}
      onClose={() => handleFormClose()}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Add New Inventory</Typography>

          {/* Barcode Scanner Icon Button */}
          <Tooltip title="Coming soon">
            <IconButton
              sx={{
                padding: '12px 16px',
                borderRadius: '12px',
                border: '1px solid #E6E6E6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              // onClick={() => setScannerOpen(true)} // Open the scanner dialog
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 400,
                  fontSize: '16px',
                  color: '#5C5C5C',
                  mr: '8px',
                }}
              >
                Scan
              </Typography>
              <DocumentScannerOutlinedIcon sx={{ fontSize: '30px' }} />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>
      <Formik
        initialValues={{
          name: '',
          upc: '',
          manufacturer: '',
          description: ' ',
          location: '',
          stock_available: '',
          stock_total: '',
          instances: '',
          stock_unit: '',
          purchase_cost: '',
          purchase_date: '',
          warranty_expiration: '',
          image: '',
          category: '',
          group: '',
          availability: '',
          status: '',
          supervised: false,
          tags: [],
          is_Hidden: false,
        }}
        validationSchema={validationSchema}
        onSubmit={handleFormSubmit}
      >
        {({
          handleSubmit,
          values,
          touched,
          errors,
          handleChange,
          setFieldValue,
          isSubmitting,
          resetForm,
        }) => (
          <Form onSubmit={handleSubmit} encType="multipart/form-data">
            <DialogContent>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormField
                    name="name"
                    label="Name of product"
                    heading="Product Name*"
                    sx={styledField}
                    headerProps={headerProps}
                  />
                  <FormField
                    name="upc"
                    label="Enter UPC"
                    heading="UPC"
                    sx={styledField}
                    headerProps={headerProps}
                  />
                  <FormField
                    name="stock_available"
                    label="Enter stock available"
                    heading="Stock Available*"
                    sx={styledField}
                    headerProps={headerProps}
                  />
                  <FormField
                    name="stock_total"
                    label="Enter stock total"
                    heading="Stock Total*"
                    sx={styledField}
                    headerProps={headerProps}
                  />
                  <FormField
                    name="instances"
                    label="Enter instances"
                    heading="Instances*"
                    sx={styledField}
                    headerProps={headerProps}
                  />
                  <FormField
                    name="manufacturer"
                    label="Enter manufacturer name"
                    heading="Manufacturer Name"
                    sx={styledField}
                    headerProps={headerProps}
                  />
                  <Box display="flex" flexDirection="column">
                    <FormField
                      name="location"
                      fieldType="dropdown"
                      heading="Location"
                      label="Select Location"
                      options={locations}
                      sx={styledField}
                      SelectProps={{
                        IconComponent: KeyboardArrowDown,
                      }}
                      addOption
                      handleAddOption={() => setOpenCustomLocation(true)}
                      headerProps={headerProps}
                    />
                  </Box>
                  <CustomLocationDialog
                    openCustomLocation={openCustomLocation}
                    setCustomLocation={setCustomLocation}
                    onClose={() => setOpenCustomLocation(false)}
                    onConfirm={() => {
                      setFieldValue('location', customLocation);
                      handleConfirmLocation();
                    }}
                  />
                  <FormField
                    name="purchase_cost"
                    label="Enter purchase cost"
                    heading="Purchase Cost"
                    sx={styledField}
                    headerProps={headerProps}
                  />
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: '400', color: '#666666' }}
                    >
                      Inventory Image
                    </Typography>
                    <RenderFileUpload
                      name="image"
                      sx={{ ...styledField, padding: '6px 8px' }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box
                    display="flex"
                    flexDirection="column"
                    position="relative"
                  >
                    <FormField
                      name="category"
                      fieldType="dropdown"
                      heading="Category"
                      options={categories}
                      sx={styledField}
                      label="Select Category"
                      SelectProps={{
                        IconComponent: KeyboardArrowDown,
                      }}
                      addOption
                      handleAddOption={() => setOpenCustomCategory(true)}
                      headerProps={headerProps}
                    />
                  </Box>
                  <CustomCategoryDialog
                    openCustomCategory={openCustomCategory}
                    setCustomCategory={setCustomCategory}
                    onClose={() => setOpenCustomCategory(false)}
                    onConfirm={() => {
                      setFieldValue('category', customCategory);
                      handleConfirmCategory();
                    }}
                  />
                  <FormField
                    name="tags"
                    fieldType="dropdown"
                    heading="Tags*"
                    label="Select Status"
                    options={taglist}
                    sx={styledField}
                    SelectProps={{
                      IconComponent: KeyboardArrowDown,
                      multiple: true,
                    }}
                    headerProps={headerProps}
                  />
                  <Box
                    display="flex"
                    flexDirection="column"
                    position="relative"
                  >
                    <FormField
                      name="group"
                      fieldType="dropdown"
                      heading="Group"
                      label="Select Group"
                      options={groups}
                      sx={styledField}
                      SelectProps={{
                        IconComponent: KeyboardArrowDown,
                      }}
                      addOption
                      handleAddOption={() => setOpenCustomGroup(true)}
                      headerProps={headerProps}
                    />
                  </Box>
                  <CustomGroupDialog
                    openCustomGroup={openCustomGroup}
                    setCustomGroup={setCustomGroup}
                    onClose={() => setOpenCustomGroup(false)}
                    onConfirm={() => {
                      setFieldValue('group', customGroup);
                      handleConfirmGroup();
                    }}
                  />
                  <FormField
                    name="stock_unit"
                    label="Enter stock unit"
                    heading="Stock Unit"
                    sx={styledField}
                    headerProps={headerProps}
                  />
                  <FormField
                    name="purchase_date"
                    label="Purchase Date"
                    fieldType="date"
                    heading="Purchase Date"
                    sx={styledField}
                    headerProps={headerProps}
                    format="YYYY-MM-DD"
                  />

                  <FormField
                    name="warranty_expiration"
                    label="Warranty Expiration"
                    fieldType="date"
                    heading="Warranty Expiration"
                    sx={styledField}
                    headerProps={headerProps}
                    format="YYYY-MM-DD"
                  />
                  <FormField
                    name="availability"
                    fieldType="dropdown"
                    heading="Availability"
                    label="Select Availability"
                    options={availabilityList}
                    sx={styledField}
                    SelectProps={{
                      IconComponent: KeyboardArrowDown,
                    }}
                    headerProps={headerProps}
                  />
                  <FormField
                    name="status"
                    fieldType="dropdown"
                    heading="Status"
                    label="Select Status"
                    options={statusList}
                    sx={styledField}
                    SelectProps={{
                      IconComponent: KeyboardArrowDown,
                    }}
                    headerProps={headerProps}
                  />
                  <Box sx={{ mb: 2 }}>
                    <FormControlLabel
                      control={
                        <Field
                          as={Checkbox}
                          name="supervised"
                          color="primary"
                          checked={values.supervised}
                          onChange={handleChange}
                        />
                      }
                      label={
                        <Typography
                          sx={{ fontSize: '1.25rem', color: '#666666' }}
                        >
                          Supervised
                        </Typography>
                      }
                      labelPlacement="end"
                    />
                    {touched.supervised && errors.supervised && (
                      <FormHelperText error>{errors.supervised}</FormHelperText>
                    )}
                  </Box>
                </Grid>
              </Grid>
              <Box sx={{ mt: 2, mb: 1 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: '400',
                    fontSize: '1.2rem',
                    color: '#666666',
                    mt: 4,
                  }}
                >
                  Description
                </Typography>
                <Field
                  as={TextField}
                  name="description"
                  label="Enter description"
                  fullWidth
                  multiline
                  rows={4}
                  variant="outlined"
                  value={values.description}
                  onChange={handleChange}
                  sx={{
                    mt: 2,
                    mb: 1,
                    '& .MuiOutlinedInput-root': { borderRadius: '12px' },
                  }}
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => handleFormClose(resetForm)}
                color="primary"
                variant="outlined"
                sx={{
                  textTransform: 'none',
                  fontWeight: 'bold',
                  borderRadius: '12px',
                  borderWidth: '1px',
                  borderColor: 'action.active',
                  '&:hover': {
                    borderWidth: '1px',
                    borderColor: 'action.active',
                  },
                  mr: 1,
                }}
                disabled={isSubmitting}
              >
                Discard
              </Button>
              <Button
                type="submit"
                variant="contained"
                disableElevation
                sx={{
                  textTransform: 'none',
                  fontWeight: 'bold',
                  color: 'common.white',
                  backgroundColor: 'primary.main',
                  borderRadius: '12px',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                }}
                disabled={isSubmitting}
              >
                Add Inventory
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default AddInventoryDialog;
